import { apiClient } from '@/lib/api/client';
import { MESSAGES } from '@lib/constants/messages';
import type { Company } from '../types/company.types';

export const companyService = {
  /**
   * Get the selected company with all details including certificates.
   *
   * Devuelve el mismo shape envuelto `{ company }` que entrega el backend
   * (ver company.controller.ts `getSelectedCompany`) y que usa
   * `features/companies/services/companyService.ts`. Ambos comparten la
   * query key de React Query `['company', 'selected']` (TenantSwitcher,
   * useCompanies, useSupportHelp, este hook), así que si alguno de los dos
   * devolviera una forma distinta, cualquiera de los dos queryFn podría
   * pisar la caché compartida con un shape que el otro consumidor no
   * espera — eso rompía intermitentemente el TenantSwitcher (compañía
   * "deseleccionada") o el nombre de compañía en el footer de AppLayout,
   * según cuál hubiera corrido último (bug Sprint 90).
   */
  async getSelectedCompany(): Promise<{ company: Company | null }> {
    const response = await apiClient.get('/companies/selected');
    const selectedCompany = response.data.company;

    return { company: selectedCompany?.id ? selectedCompany : null };
  },

  /**
   * Imagen del logo de la compania seleccionada, proxeada por el BFF
   * (`GET /companies/selected/logo`). Se pide como blob y no se usa la URL
   * del blob de Aludra directamente porque ese host no manda CORS: un <img>
   * la muestra, pero canvas no puede leer sus pixeles. El sidebar los lee
   * para sacar el color de la marca (ver `CompanyBrandLogo`).
   */
  async getSelectedCompanyLogoBlob(): Promise<Blob> {
    const response = await apiClient.get(`/companies/selected/logo?_t=${Date.now()}`, { responseType: 'blob' });
    return response.data as Blob;
  },

  /**
   * Get company settings by ID
   * Uses the selected company ID from the session
   * This method fetches full company details from Aludra API for editing
   */
  async getCompanySettings(): Promise<Company> {
    // First get the selected company to get its ID
    const selectedResponse = await apiClient.get('/companies/selected');
    const selectedCompany = selectedResponse.data.company;

    if (!selectedCompany?.id) {
      throw new Error(MESSAGES.NO_COMPANY_SELECTED);
    }

    // Then fetch the full company details from Aludra
    const response = await apiClient.get(`/companies/${selectedCompany.id}`);
    return response.data;
  },

  /**
   * Update company settings
   */
  async updateCompanySettings(settings: Partial<Company>): Promise<Company> {
    // First get the selected company to get its ID
    const selectedResponse = await apiClient.get('/companies/selected');
    const selectedCompany = selectedResponse.data.company;

    if (!selectedCompany?.id) {
      throw new Error(MESSAGES.NO_COMPANY_SELECTED);
    }

    // Update the company
    const response = await apiClient.put(`/companies/${selectedCompany.id}`, settings);
    return response.data;
  },

  /**
   * Get country information by countryId
   */
  async getCountryById(countryId: string): Promise<any> {
    const response = await apiClient.get(`/companies/country/${countryId}`);
    return response.data;
  },

  /**
   * Upload a file to Aludra cloud storage via BFF
   * @param file - The file to upload
   * @param companyId - The company ID
   * @param entityType - The entity type (default: 'customer')
   * @returns The uploaded file URL
   */
  async uploadFile(
    file: File,
    companyId: string,
    entityType: string = 'customer'
  ): Promise<{ url: string }> {
    const tenantId = import.meta.env.VITE_DEFAULT_TENANT || '172';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    formData.append('entityType', entityType);
    formData.append('tenantId', tenantId);

    console.log('📤 Uploading file via BFF:', {
      originalName: file.name,
      companyId,
      entityType,
      tenantId,
      size: file.size,
    });

    const response = await apiClient.post(
      '/companies/upload-file',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    console.log('✅ File uploaded successfully:', response.data);

    return response.data;
  },
};
